import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './Wiki.css';
import { useAuth } from '../../hooks/useAuth'; // Добавляем импорт useAuth

const WikiEditor = ({ document, mode, onSave, onCancel }) => {
  const { user } = useAuth(); // Получаем пользователя
  const [form, setForm] = useState({
    title: '',
    content: ''
  });
  const [error, setError] = useState('');
  const quillRef = useRef(null);

  useEffect(() => {
    // Инициализация кастомного формата изображения
    if (typeof window !== 'undefined' && window.Quill) {
      const Quill = window.Quill;
      const ImageBlot = Quill.import('formats/image');
      
      class ResizableImageBlot extends ImageBlot {
        static create(value) {
          const node = super.create(value);
          
          if (typeof value === 'string') {
            node.setAttribute('src', value);
            node.style.maxWidth = '100%';
            node.style.height = 'auto';
          }
          
          return node;
        }
      }

      ResizableImageBlot.blotName = 'image';
      ResizableImageBlot.tagName = 'IMG';
      Quill.register(ResizableImageBlot, true);
    }
  }, []);

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      
      // Добавляем обработчик вставки
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        if (node.tagName === 'IMG') {
          const src = node.getAttribute('src');
          if (src && src.startsWith('data:image')) {
            // Обработка base64 изображений
            return delta.insert({ image: src });
          }
        }
        return delta;
      });
    }
  }, []);

  useEffect(() => {
    if (document && mode === 'edit') {
      setForm({
        title: document.title || '',
        content: document.content || ''
      });
    } else {
      setForm({
        title: '',
        content: ''
      });
    }
  }, [document, mode]);

  // Функция для вставки изображений из буфера обмена
  const handlePaste = (event) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const clipboardItems = event.clipboardData?.items;
    if (!clipboardItems) return;

    // Проверяем, есть ли изображения в буфере обмена
    for (const item of clipboardItems) {
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          
          reader.onload = (e) => {
            const imageUrl = e.target.result;
            const range = quill.getSelection();
            
            // Вставляем изображение
            quill.insertEmbed(range.index, 'image', imageUrl);
            
            // Добавляем пробел после изображения для удобства
            quill.insertText(range.index + 1, '\n');
            
            // Обновляем содержимое формы
            const content = quill.root.innerHTML;
            setForm(prev => ({ ...prev, content }));
          };
          
          reader.readAsDataURL(file);
        }
        break; // Обрабатываем только первое изображение
      }
    }
  };

  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false,
    }
  };

  function imageHandler() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    
    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          const reader = new FileReader();
          
          reader.onload = (e) => {
            const imageUrl = e.target.result;
            const quill = quillRef.current?.getEditor();
            const range = quill.getSelection();
            
            // Вставляем изображение
            quill.insertEmbed(range.index, 'image', imageUrl);
            
            // Добавляем пробел после изображения для удобства
            quill.insertText(range.index + 1, '\n');
            
            // Обновляем содержимое формы
            const content = quill.root.innerHTML;
            setForm(prev => ({ ...prev, content }));
          };
          
          reader.readAsDataURL(file);
          
        } catch (error) {
         
          alert('Ошибка при добавлении изображения');
        }
      }
    };
  }

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      setError('Введите название документа');
      return;
    }

    if (form.title.length > 20) {
      setError('Название документа должно быть не более 20 символов');
      return;
    }

    if (!form.content.trim()) {
      setError('Введите содержимое документа');
      return;
    }

    setError('');
    
    // СОЗДАЕМ ДАННЫЕ ДЛЯ ОТПРАВКИ
    const documentData = {
      title: form.title.trim(),
      content: form.content,
      created_by: user?.id // ДОБАВЛЯЕМ ID ПОЛЬЗОВАТЕЛЯ
    };
    

    
    if (mode === 'create') {
      onSave(documentData); // Теперь передаем documentData с created_by
    } else {
      // Для редактирования можно не передавать created_by
      onSave(document.id, {
        title: form.title.trim(),
        content: form.content
      });
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  return (
    <div className="wiki-editor-page">
      <div className="editor-header">
        <h2>
          <i className="fas fa-edit"></i>
          {mode === 'create' ? 'Создание документа' : 'Редактирование документа'}
        </h2>
        
        <div className="editor-actions">
          <button className="add-button" onClick={handleSubmit}>
            <i className="fas fa-save"></i>
            Сохранить
          </button>
        </div>
      </div>

      <form className="editor-form" onSubmit={handleSubmit}>
        {error && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle"></i>
            {error}
          </div>
        )}

        <div className="form-group">
          <label>Название документа *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Введите название документа..."
            maxLength={20}
            required
          />
          <div className="char-counter">
            {form.title.length}/20 символов
          </div>
        </div>

        <div className="form-group">
          <div 
            className="quill-container"
            onPaste={handlePaste}
          >
            <ReactQuill
              ref={quillRef}
              value={form.content}
              onChange={(content) => handleChange('content', content)}
              modules={modules}
              formats={formats}
              placeholder="Введите содержимое документа..."
              theme="snow"
            />
          </div>
        </div>

        <div className="editor-help">
          <h4>Советы по редактированию:</h4>
          <ul>
            <li>Для добавления изображения нажмите на значок изображения в панели инструментов</li>
            <li>Можно вставлять изображения через Ctrl+V (копировать из браузера или файловой системы)</li>
            <li>Изображения можно перетаскивать для изменения положения</li>
            <li>Размер изображения автоматически адаптируется</li>
            <li>Поддерживаются форматы: JPG, PNG, GIF, SVG</li>
          </ul>
        </div>
      </form>
    </div>
  );
};

export default WikiEditor;